/**
 * SummonSkillEffect — Conjuration foundation active: conjure a wisp.
 *
 * Spawns a single wisp 1 tile ahead of the player's facing direction.
 * The wisp drifts toward the nearest enemy and draws ~60% of enemy aggro
 * away from the player. No duration — persists until killed. Fragile (10 HP
 * baseline). No maximum wisp count at this tier.
 *
 * Upgrade hooks (resolved here, values come from UpgradeEffectResolver):
 * - Outstretched (summon_reach): spawnDistanceTiles additive (+1 tile)
 * - Outstretched (summon_reach): driftSpeedMultiplier multiplicative (×1.25)
 * - Tenacious (summon_tenacity): hpMultiplier multiplicative (×1.4)
 * - Tenacious (summon_tenacity): aggroRedirectChance max (0.75 override)
 *
 * Spawn location is checked for wall collision — if blocked, falls back to
 * spawning at the player's position.
 *
 * Fast cooldown tier (800ms). Pure logic — no Phaser dependencies.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see conjuration-spec.md § Foundation Node — Summon
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

/** Config object passed to SummonSystem.spawn() for wisp summons. */
export interface WispSummonConfig {
  readonly id: string;
  readonly archetype: 'wisp';
  readonly baseHp: number;
  readonly baseDamage: number;
  readonly baseSpeed: number;
  readonly leashRadiusTiles: number;
  readonly aggroWeight: number;
  /**
   * AI movement mode. 'drift' = simple vector toward nearest target (Summon tier).
   * 'tracking' = A* pathfinding (Invoke tier).
   * 'coordinated' = load-balanced multi-target coverage (Overwhelm tier).
   * 'encircle' = distribute around enemy cluster centroid at 2-tile radius (Apotheosis tier).
   * Consumed by the integration layer; SummonSystem itself is movement-mode agnostic.
   */
  readonly aiMode?: 'drift' | 'tracking' | 'coordinated' | 'encircle';
  /**
   * For 'coordinated' AI mode: the enemy ID this wisp is assigned to track.
   * Set by OverwhelmSkillEffect based on the coverage map at spawn time.
   * Undefined if no enemies are visible or aiMode is not 'coordinated'.
   */
  readonly assignedTargetId?: string;
}

export interface SummonDeps {
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
    getMultiplicativeEffect(skillId: string, key: string, base?: number): number;
    getMaxEffect(skillId: string, key: string): number | undefined;
  };
  readonly player: {
    getPosition(): { x: number; y: number };
    getFacingDirection(): { x: number; y: number };
  };
  readonly collision: {
    isWallAt(x: number, y: number): boolean;
  };
  readonly summons: {
    spawn(config: WispSummonConfig, x: number, y: number): string;
  };
}

// ---------------------------------------------------------------------------
// Skill tuning
// ---------------------------------------------------------------------------

export interface SummonSkillConfig {
  readonly cooldownMs: number;
  /** Base wisp HP before upgrade multipliers. */
  readonly baseHp: number;
  /** Base aggro weight (fraction of total aggro drawn to wisp). */
  readonly baseAggroWeight: number;
  /** Base spawn distance in tiles from player position. */
  readonly baseSpawnDistanceTiles: number;
  /** Normalized base drift speed (integration layer applies px/s). */
  readonly baseDriftSpeed: number;
  /** Maximum follow distance before wisp returns toward player (tiles). */
  readonly leashRadiusTiles: number;
  /** Tile size in pixels — used for spawn position calculation. */
  readonly tileSize: number;
}

const DEFAULT_CONFIG: SummonSkillConfig = {
  cooldownMs: 800,               // fast cooldown tier
  baseHp: 10,                    // spec § Summon HP: 10 HP baseline
  baseAggroWeight: 0.6,          // 60% aggro redirect at baseline
  baseSpawnDistanceTiles: 1,     // spawn 1 tile ahead of player
  baseDriftSpeed: 1.0,           // multiplied by integration layer's drift speed px/s
  leashRadiusTiles: 8,           // reasonable follow range
  tileSize: 32,                  // TILE_SIZE constant from Constants.ts
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class SummonSkillEffect implements SkillEffect {
  readonly skillId = 'summon';
  readonly type = 'active' as const;
  readonly activationMode = 'instant' as const;
  readonly failLabel = 'FIZZLE';

  private readonly deps: SummonDeps;
  private readonly config: SummonSkillConfig;

  private _cooldownRemaining = 0;

  // Observable state for scenarios and integration layer
  private _spawnsCount = 0;
  private _lastSpawnedId = '';
  private _lastSpawnX = 0;
  private _lastSpawnY = 0;
  private _lastSpawnWasFallback = false;
  private _resolvedHp = 0;
  private _resolvedAggroWeight = 0;
  private _resolvedDriftSpeed = 0;
  private _resolvedSpawnDistanceTiles = 0;

  constructor(deps: SummonDeps, config: Partial<SummonSkillConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters ---

  get cooldown(): number { return this._cooldownRemaining; }
  get spawnsCount(): number { return this._spawnsCount; }
  get lastSpawnedId(): string { return this._lastSpawnedId; }
  get lastSpawnX(): number { return this._lastSpawnX; }
  get lastSpawnY(): number { return this._lastSpawnY; }
  get lastSpawnWasFallback(): boolean { return this._lastSpawnWasFallback; }
  get resolvedHp(): number { return this._resolvedHp; }
  get resolvedAggroWeight(): number { return this._resolvedAggroWeight; }
  get resolvedDriftSpeed(): number { return this._resolvedDriftSpeed; }
  get resolvedSpawnDistanceTiles(): number { return this._resolvedSpawnDistanceTiles; }

  // --- SkillEffect interface ---

  canActivate(): boolean {
    return this._cooldownRemaining <= 0;
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    const pos = this.deps.player.getPosition();
    const facing = this.deps.player.getFacingDirection();

    // Resolve spawn distance: base + additive upgrade (Outstretched)
    const spawnDistanceBonus = this.deps.resolver.getAdditiveEffect('summon', 'spawnDistanceTiles');
    const distanceTiles = this.config.baseSpawnDistanceTiles + spawnDistanceBonus;
    this._resolvedSpawnDistanceTiles = distanceTiles;

    const spawnX = pos.x + facing.x * distanceTiles * this.config.tileSize;
    const spawnY = pos.y + facing.y * distanceTiles * this.config.tileSize;

    // Wall collision fallback: spawn at player position if target tile is blocked
    let finalX = spawnX;
    let finalY = spawnY;
    let wasFallback = false;
    if (this.deps.collision.isWallAt(spawnX, spawnY)) {
      finalX = pos.x;
      finalY = pos.y;
      wasFallback = true;
    }

    // Resolve HP: base × hpMultiplier (Tenacious)
    const hpMultiplier = this.deps.resolver.getMultiplicativeEffect('summon', 'hpMultiplier', 1.0);
    const resolvedHp = Math.round(this.config.baseHp * hpMultiplier);
    this._resolvedHp = resolvedHp;

    // Resolve aggro weight: max of baseline and aggroRedirectChance override (Tenacious)
    const aggroOverride = this.deps.resolver.getMaxEffect('summon', 'aggroRedirectChance');
    const resolvedAggroWeight = aggroOverride !== undefined
      ? Math.max(this.config.baseAggroWeight, aggroOverride)
      : this.config.baseAggroWeight;
    this._resolvedAggroWeight = resolvedAggroWeight;

    // Resolve drift speed: base × driftSpeedMultiplier (Outstretched)
    const driftMultiplier = this.deps.resolver.getMultiplicativeEffect('summon', 'driftSpeedMultiplier', 1.0);
    const resolvedDriftSpeed = this.config.baseDriftSpeed * driftMultiplier;
    this._resolvedDriftSpeed = resolvedDriftSpeed;

    const wispConfig: WispSummonConfig = {
      id: 'summon_wisp',
      archetype: 'wisp',
      baseHp: resolvedHp,
      baseDamage: 0,
      baseSpeed: resolvedDriftSpeed,
      leashRadiusTiles: this.config.leashRadiusTiles,
      aggroWeight: resolvedAggroWeight,
    };

    this._lastSpawnedId = this.deps.summons.spawn(wispConfig, finalX, finalY);
    this._spawnsCount++;
    this._lastSpawnX = finalX;
    this._lastSpawnY = finalY;
    this._lastSpawnWasFallback = wasFallback;

    this._cooldownRemaining = this.config.cooldownMs;

    return true;
  }

  deactivate(): void {
    // Instant skill — no toggle state
  }

  isActive(): boolean {
    return false;
  }

  update(delta: number): void {
    if (this._cooldownRemaining > 0) {
      this._cooldownRemaining = Math.max(0, this._cooldownRemaining - delta);
    }
  }

  destroy(): void {
    this._cooldownRemaining = 0;
  }
}
